/**
 * "1-slot overlap" — whether auto-assign may hand a counter over with the outgoing
 * and incoming officer both on it for one 15-minute slot, when that mans more
 * counters without a break in service.
 *
 * Unlike "Max ctr duration", this belongs to the ROSTER, not to the browser: two
 * supervisors working the same session ID must plan against the same rule, so it
 * lives on the session server-side and is read back from it. The module keeps one
 * cached copy so the several headers that show the checkbox all show the same
 * state and all update together, and so switching session re-reads it rather than
 * carrying the last session's answer across.
 *
 * Off is the default and means the algorithm is exactly what it was before the
 * option existed: a counter counts as running only when handed over back-to-back.
 */

import { useEffect, useState } from 'react';
import { apiClient } from '@/api/client';

let current = false;
/** Undefined until the first read from the server lands (or a state payload
 *  supplies it), so the checkbox can wait rather than flash the wrong answer. */
let loaded = false;
let inFlight: Promise<void> | null = null;

const listeners = new Set<(value: boolean) => void>();

function broadcast(): void {
  listeners.forEach((listener) => listener(current));
}

/**
 * Adopt the value that came back with a roster-state payload. Cheaper than a
 * round-trip of our own, and keeps the checkbox honest when another user in the
 * same session flips it.
 */
export function adoptHandoffOverlap(value: boolean | undefined): void {
  if (value === undefined) return;
  loaded = true;
  if (value === current) return;
  current = value;
  broadcast();
}

/** Forget what we know — call when the session changes, so the next read asks
 *  the new session rather than answering for the old one. */
export function resetHandoffOverlap(): void {
  loaded = false;
  inFlight = null;
  if (current) {
    current = false;
    broadcast();
  }
}

async function load(): Promise<void> {
  if (loaded || inFlight) return inFlight ?? undefined;
  inFlight = apiClient
    .getHandoffOverlap()
    .then(({ allow_handoff_overlap }) => {
      loaded = true;
      if (allow_handoff_overlap !== current) {
        current = allow_handoff_overlap;
        broadcast();
      }
    })
    .catch(() => { /* no roster yet, or offline — the default stands */ })
    .finally(() => { inFlight = null; });
  return inFlight;
}

/**
 * The session's setting, live across every checkbox showing it. The setter writes
 * through to the session immediately and rolls back if the write fails, so what the
 * box shows is always what the next auto-assign will do.
 */
export function useHandoffOverlap(): [boolean, (value: boolean) => Promise<void>, boolean] {
  const [value, setValue] = useState(current);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    listeners.add(setValue);
    setValue(current);
    void load();
    return () => { listeners.delete(setValue); };
  }, []);

  const set = async (next: boolean): Promise<void> => {
    if (next === current) return;
    const previous = current;
    current = next;
    loaded = true;
    broadcast();
    setSaving(true);
    try {
      await apiClient.setHandoffOverlap(next);
    } catch {
      current = previous;
      broadcast();
    } finally {
      setSaving(false);
    }
  };

  return [value, set, saving];
}
